import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { ChatHistory } from './ChatHistory';
import { ChatInput } from './ChatInput';
import { useChat } from '../../hooks/useChat';
import { EmptyState } from '../common/EmptyState';
import { Chat as ChatIcon } from '@mui/icons-material';

interface ChatInterfaceProps {
  deckId: string;
  slideNumber?: number | string;  // Optional: for slide-specific chat (accepts both for flexibility)
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ deckId, slideNumber }) => {
  const { messages, loading, sending, error, sendMessage, refresh, newMessageId, typingContent, typingMessageId } = useChat(deckId, slideNumber);

  if (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : typeof error === 'string' 
        ? error 
        : JSON.stringify(error, null, 2);
    
    return (
      <Paper sx={{ p: 3 }}>
        <Alert 
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={refresh}>
              Retry
            </Button>
          }
        >
          <Typography variant="body1" fontWeight={600}>
            Failed to load chat history
          </Typography>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem' }}>
            {errorMessage}
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%', // Match parent Grid height
      }}
    >
      <Paper
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6">
            AI Analysis Assistant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {slideNumber 
              ? `Ask questions about Slide ${slideNumber}`
              : 'Ask questions about your presentation analysis'}
          </Typography>
        </Box>

        {messages.length === 0 && !loading ? (
          <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
            <EmptyState
              icon={<ChatIcon />}
              title="Start a Conversation"
              description="Ask the AI assistant about your presentation analysis, request clarifications, or get suggestions for improvements."
            />
          </Box>
        ) : (
          <ChatHistory 
            messages={messages} 
            loading={loading || (sending && !typingMessageId)} 
            newMessageId={newMessageId}
            typingContent={typingContent}
            typingMessageId={typingMessageId}
          />
        )}

        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <ChatInput
            onSendMessage={sendMessage}
            disabled={sending}
          />
        </Box>
      </Paper>
    </Box>
  );
};

